import {
  getAvatarGradientStops,
  getAvatarTextColor
} from "@gettactic/helpers/src/colors";
import { classNames } from "@gettactic/helpers/src/css/classNames";
import {
  InteractiveZone,
  InteractiveZoneCircle
} from "@gettactic/helpers/src/map/types";
import React from "react";

export interface CircleProfilePhotoProps extends React.PropsWithChildren {
  zone: InteractiveZone;
  title: string;
  label?: string;
  className?: string;
  isFaded?: boolean;
  bgColor?: string;
}

/** A round badge over a map spot, with a `label` (initials or a number) and a `title`. */
export function InteractiveActiveSpot({
  zone,
  title,
  children,
  className,
  label,
  isFaded,
  bgColor
}: CircleProfilePhotoProps) {
  const circle = zone.circle;
  const { strokeWidth, transform, cx, cy, r } = circle as InteractiveZoneCircle;

  const groupTransform = React.useMemo(() => {
    if (!transform) return "";

    const pattern =
      /matrix\(.*? 1 1 .*? (\d+(\.\d+)?(e-?\d+)?) (\d+(\.\d+)?(e-?\d+)?)\) rotate\(-90 .*?\)/;
    const match = transform.match(pattern);

    if (match) {
      const x = match[1];
      const y = match[4];
      return `translate(${x} ${y}) rotate(0)`;
    }

    return transform;
  }, [transform]);

  // Counter-rotate content so profile photos and text stay upright
  const counterRotation = zone.rotation
    ? `rotate(${-zone.rotation}, ${cx ?? 0}, ${cy ?? 0})`
    : undefined;

  // Gradient fallback fill, mirroring the HTML avatar's `getAvatarGradient`.
  // The id is derived from the background color (not `useId`) so it stays
  // deterministic across the map's timing-sensitive re-renders/re-parses.
  const gradientStops = bgColor ? getAvatarGradientStops(bgColor) : null;
  const gradientId = bgColor
    ? `avatar-grad-${bgColor.replace(/[^a-z0-9]/gi, "")}`
    : undefined;
  const fillStyle = gradientStops ? { fill: `url(#${gradientId})` } : undefined;

  return (
    <g
      transform={
        zone.mapBuilderContainerTransformation || zone.containerTransformation
      }
      className={classNames("circle-profile-photo", className)}
      opacity={isFaded ? 0.5 : 1}
    >
      <g transform={counterRotation}>
        {gradientStops && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={gradientStops.light} />
              <stop offset="1" stopColor={gradientStops.dark} />
            </linearGradient>
          </defs>
        )}
        <circle {...circle} strokeWidth="0" fill="transparent" />
        <title>{title}</title>
        <circle {...circle} stroke="white" fill="transparent" />
        <circle
          {...circle}
          r={r - strokeWidth / 2}
          strokeWidth="2"
          stroke="#9CA3AF"
          fill="transparent"
          style={fillStyle}
        />
        <circle
          className="wrapper-circle"
          {...circle}
          stroke="white"
          fill="transparent"
          style={fillStyle}
        />
        <g transform={groupTransform}>
          <text
            x={cx}
            y={transform ? (cy ?? 1 + 2) : (cy ?? 1 + r / 6) + 2}
            textAnchor="middle"
            fontSize={r / 1.3}
            // SVG text defaults to the browser serif at normal weight; the
            // HTML avatar renders its initials `font-inter font-semibold`.
            fontFamily="var(--font-inter, Inter, sans-serif)"
            fontWeight={600}
            fill={bgColor ? getAvatarTextColor(bgColor) : "white"}
            dominantBaseline="middle"
          >
            {label || title}
          </text>
        </g>
        {children}
      </g>
    </g>
  );
}
